import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { clsx } from 'clsx';

export interface TableColumn<T = any> {
    key: keyof T | string;
    title: string;
    render?: (value: any, record: T) => React.ReactNode;
    sortable?: boolean;
    width?: string | number;
    align?: 'left' | 'center' | 'right';
}

export interface TableProps<T = any> {
    columns: TableColumn<T>[];
    data: T[];
    loading?: boolean;
    pagination?: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize: number) => void;
        showSizeChanger?: boolean;
        pageSizeOptions?: number[];
    };
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    onSort?: (column: string, order: 'asc' | 'desc') => void;
    className?: string;
    rowKey?: keyof T | ((record: T) => string);
}

export const Table = <T extends Record<string, any>>({
                                                         columns,
                                                         data,
                                                         loading = false,
                                                         pagination,
                                                         sortBy,
                                                         sortOrder,
                                                         onSort,
                                                         className,
                                                         rowKey = 'id'
                                                     }: TableProps<T>) => {
    const getRowKey = (record: T, index: number): string => {
        if (typeof rowKey === 'function') {
            return rowKey(record);
        }
        return record[rowKey] || index.toString();
    };

    const handleSort = (column: TableColumn<T>) => {
        if (!column.sortable || !onSort) return;

        const columnKey = column.key as string;
        let newOrder: 'asc' | 'desc' = 'asc';

        if (sortBy === columnKey) {
            newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }

        onSort(columnKey, newOrder);
    };

    const getSortIcon = (column: TableColumn<T>) => {
        if (!column.sortable) return null;

        const columnKey = column.key as string;
        if (sortBy !== columnKey) {
            return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
        }

        return sortOrder === 'asc'
            ? <ChevronUp className="h-4 w-4 text-gray-600" />
            : <ChevronDown className="h-4 w-4 text-gray-600" />;
    };

    if (loading) {
        return (
            <div className="animate-pulse">
                <div className="h-10 bg-gray-200 rounded mb-4"></div>
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-100 rounded mb-2"></div>
                ))}
            </div>
        );
    }

    return (
        <div className={clsx('overflow-hidden', className)}>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.key as string}
                                onClick={() => handleSort(column)}
                                className={clsx(
                                    'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                                    column.sortable && 'cursor-pointer hover:bg-gray-100',
                                    column.align === 'center' && 'text-center',
                                    column.align === 'right' && 'text-right'
                                )}
                                style={{ width: column.width }}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>{column.title}</span>
                                    {getSortIcon(column)}
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((record, index) => (
                        <tr key={getRowKey(record, index)} className="hover:bg-gray-50">
                            {columns.map((column) => {
                                const value = record[column.key as keyof T];
                                const cellContent = column.render ? column.render(value, record) : value;

                                return (
                                    <td
                                        key={column.key as string}
                                        className={clsx(
                                            'px-6 py-4 whitespace-nowrap text-sm text-gray-900',
                                            column.align === 'center' && 'text-center',
                                            column.align === 'right' && 'text-right'
                                        )}
                                    >
                                        {cellContent}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {pagination && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => pagination.onChange(pagination.current - 1, pagination.pageSize)}
                            disabled={pagination.current <= 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => pagination.onChange(pagination.current + 1, pagination.pageSize)}
                            disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing{' '}
                                <span className="font-medium">
                  {(pagination.current - 1) * pagination.pageSize + 1}
                </span>{' '}
                                to{' '}
                                <span className="font-medium">
                  {Math.min(pagination.current * pagination.pageSize, pagination.total)}
                </span>{' '}
                                of{' '}
                                <span className="font-medium">{pagination.total}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => pagination.onChange(pagination.current - 1, pagination.pageSize)}
                                    disabled={pagination.current <= 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => pagination.onChange(pagination.current + 1, pagination.pageSize)}
                                    disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};